import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { ShieldCheck, Mail, MessageSquare, Bell, CheckCircle2, XCircle, Clock, Lock } from 'lucide-react';
import { format } from 'date-fns';

export default function EmailSafety() {
  const { user } = useAuth();
  const [checkTime] = useState(() => new Date());
  const isAdmin = user?.role === 'Admin' || user?.role === 'Owner';
  const isManager = user?.role === 'Manager';

  // Staff should not see this page
  if (!isAdmin && !isManager) {
    return (
      <div className="px-6 md:px-8 py-12 max-w-2xl mx-auto text-center">
        <Lock className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
        <h1 className="text-lg font-bold text-foreground mb-2">Access Restricted</h1>
        <p className="text-sm text-muted-foreground">This page is only available to management.</p>
      </div>
    );
  }

  const StatusRow = ({ icon: Icon, label, status, statusText, statusColor }: {
    icon: React.FC<{ className?: string }>;
    label: string;
    status: 'on' | 'off' | 'disabled';
    statusText: string;
    statusColor: string;
  }) => (
    <div className="flex items-center justify-between py-3 border-b border-border last:border-0">
      <div className="flex items-center gap-3">
        <Icon className="w-4 h-4 text-muted-foreground" />
        <span className="text-sm text-foreground">{label}</span>
      </div>
      <div className="flex items-center gap-2">
        {status === 'on' ? (
          <CheckCircle2 className={`w-4 h-4 ${statusColor}`} />
        ) : (
          <XCircle className={`w-4 h-4 ${statusColor}`} />
        )}
        <span className={`text-sm font-medium ${statusColor}`}>{statusText}</span>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-card border-b border-border">
        <div className="px-6 md:px-8 py-5 flex items-center gap-3">
          <ShieldCheck className="w-5 h-5 text-primary" />
          <div>
            <h1 className="text-lg font-bold text-foreground">Email & Notification Safety</h1>
            <p className="text-xs text-muted-foreground mt-0.5">Outbound communication status for the Staff Hub</p>
          </div>
        </div>
      </div>

      <div className="px-6 md:px-8 py-6 max-w-2xl mx-auto space-y-6">
        {/* Kill Switch Status */}
        <div className="border-2 border-primary/30 bg-primary/5 rounded-lg p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-base font-bold text-foreground">Email Kill Switch</h2>
              <p className="text-xs text-primary font-semibold uppercase tracking-wider">Active — ON</p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            The email kill switch (<code className="text-xs bg-muted px-1.5 py-0.5 rounded">ZITE_EMAIL_DELIVERY_DISABLED</code>) is set to <strong className="text-foreground">true</strong>.
            All outbound email from this app is suppressed. No staff notifications, emails, or automated messages will be sent.
          </p>
        </div>

        {/* Channel Status */}
        <div className="border border-border bg-card rounded-lg p-5">
          <h3 className="text-sm font-semibold text-foreground mb-3">Outbound Channel Status</h3>
          <StatusRow icon={Mail} label="Email (Staff Notifications)" status="off" statusText="Suppressed" statusColor="text-muted-foreground" />
          <StatusRow icon={Mail} label="Email (Admin Alerts)" status="off" statusText="Suppressed" statusColor="text-muted-foreground" />
          <StatusRow icon={MessageSquare} label="SMS (Twilio)" status="off" statusText="Not Sending" statusColor="text-muted-foreground" />
          <StatusRow icon={Bell} label="Push Notifications" status="off" statusText="Not Enabled" statusColor="text-muted-foreground" />
          <StatusRow icon={Bell} label="In-App Notifications" status="on" statusText="Active (internal only)" statusColor="text-primary" />
        </div>

        {/* What This Means */}
        <div className="border border-border bg-card rounded-lg p-5">
          <h3 className="text-sm font-semibold text-foreground mb-3">What This Means</h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <XCircle className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
              <span>No staff will receive email notifications from the Staff Hub.</span>
            </li>
            <li className="flex items-start gap-2">
              <XCircle className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
              <span>No SMS messages will be sent to staff mobile numbers.</span>
            </li>
            <li className="flex items-start gap-2">
              <XCircle className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
              <span>No automated email (edit request approvals, compliance reminders, etc.) will be delivered.</span>
            </li>
            <li className="flex items-start gap-2">
              <XCircle className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
              <span>No push notifications are configured or active.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />
              <span>In-app notifications (bell icon) continue to work internally.</span>
            </li>
          </ul>
        </div>

        {/* Last Check */}
        <div className="border border-border bg-card rounded-lg p-5">
          <div className="flex items-center gap-2 text-sm">
            <Clock className="w-4 h-4 text-muted-foreground" />
            <span className="text-muted-foreground">Last status check:</span>
            <span className="text-foreground font-medium">{format(checkTime, 'dd MMM yyyy, HH:mm')}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Status is determined by the <code className="text-xs bg-muted px-1.5 py-0.5 rounded">ZITE_EMAIL_DELIVERY_DISABLED</code> environment variable.
            {isAdmin ? ' Owner/Admin can manage this via the app settings.' : ' Contact an Admin to change this setting.'}
          </p>
        </div>

        {isManager && !isAdmin && (
          <div className="bg-muted/40 border border-border rounded-lg px-4 py-3 text-xs text-muted-foreground">
            <strong className="text-foreground">Read-only view.</strong> Only Owner/Admin can change email and notification settings.
          </div>
        )}
      </div>
    </div>
  );
}
